import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { type FillCheck, invalidateFillApplication } from '@/models/fillApplication'
import { dispatchBlockedSecondsAgo } from '@/models/insuranceApplication'
import { useRequestRequote } from '@/models/requote'
import { MissingItemsList } from './MissingItemsList'

interface SentRequoteButtonProps {
  orgId: string
  applicationId: string
  /** The same missing items Send checks; a requote waits for them. */
  blocking: FillCheck[]
}

export function SentRequoteButton({ orgId, applicationId, blocking }: SentRequoteButtonProps) {
  const queryClient = useQueryClient()
  const requote = useRequestRequote(applicationId)
  const [open, setOpen] = useState(false)
  const [blockedSecondsAgo, setBlockedSecondsAgo] = useState<number | null>(null)

  async function send(force: boolean) {
    try {
      const result = await requote.mutateAsync({ force })
      setOpen(false)
      toast.success('Asking carriers for new prices')
      result.requoteNotices?.forEach((notice) => toast.warning(notice))
      await invalidateFillApplication(queryClient, orgId)
    } catch (error) {
      const secondsAgo = dispatchBlockedSecondsAgo(error)
      if (secondsAgo !== null) {
        setOpen(false)
        setBlockedSecondsAgo(secondsAgo)
        return
      }
      toast.error("Couldn't request new quotes. Please try again.")
    }
  }

  return (
    <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)}>
        <RefreshCw /> Requote
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Requote this customer?</DialogTitle>
            <DialogDescription>
              {blocking.length > 0
                ? 'Fix these first: carriers would turn the application away without them.'
                : 'The application goes back out to every carrier and its quotes are replaced with new ones.'}
            </DialogDescription>
          </DialogHeader>
          {blocking.length > 0 && <MissingItemsList items={blocking} onJump={() => setOpen(false)} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void send(false)}
              disabled={blocking.length > 0}
              loading={requote.isPending ? 'Requesting…' : undefined}
            >
              Send to every carrier again
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={blockedSecondsAgo !== null}
        onOpenChange={(next) => {
          if (!next) setBlockedSecondsAgo(null)
        }}
        title="Send again?"
        description={`Rocklake was sent ${blockedSecondsAgo ?? 0} seconds ago. Re-quoting now creates another quote in the carrier's portal.`}
        confirmLabel="Send again"
        onConfirm={() => send(true)}
      />
    </>
  )
}
